import React, { useEffect } from 'react';
import { gql } from '@apollo/client';
import { useToast } from '../../../../hooks/useToast';
import { Icon } from '../../../../blocks/icons';
import { useBlockingRouter } from '../../../../hooks/useBlockingRouter';
import {
	InvoiceViewButtonBarExportButtonInvoiceFragment,
	useInvoiceViewButtonBar_ExportInvoiceLazyQuery,
} from '#types';
import { ButtonBarButtonsProp } from '../../../ButtonBar';
import { Differences } from '../../../../lib/helpers/deepCompareAndDiff';

export const invoiceViewButtonBarExportButtonInvoiceFragment = gql`
	fragment invoiceViewButtonBarExportButtonInvoice on Invoice {
		id
	}
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const invoiceViewButtonBar_exportInvoice = gql`
	query invoiceViewButtonBar_exportInvoice(
		$where: InvoiceWhereUniqueInput!
		$data: InvoiceExportInput!
	) {
		exportInvoice(where: $where, data: $data) {
			url
		}
	}
`;

export type UseInvoiceViewButtonBarExportButtonReturnType = {
	button: ButtonBarButtonsProp['buttons'];
	exportButtonIsLoading: boolean;
};

export type UseInvoiceViewButtonBarExportButtonProps = {
	invoice?: InvoiceViewButtonBarExportButtonInvoiceFragment;
	guestToken?: string;
	invoiceEdits?: Differences;
};

export const useInvoiceViewButtonBarExportButton = ( {
	invoice,
	guestToken,
	invoiceEdits,
}: UseInvoiceViewButtonBarExportButtonProps ): UseInvoiceViewButtonBarExportButtonReturnType => {
	const { showError } = useToast();
	const router = useBlockingRouter();

	const [ exportInvoice, { loading, error } ] =
		useInvoiceViewButtonBar_ExportInvoiceLazyQuery( {
			/* Tell Apollo to not update the cache with the response from this query. If we let
			Apollo update cache, then the MuiEmbeddedFile component, wil try to redraw the new file
			while we navigate. which causes a PUN to show up in non-Chrome browsers */
			fetchPolicy: 'no-cache',
		} );

	useEffect( () => {
		if ( error ) {
			showError( error );
		}
	}, [ error, showError ] );

	const onClick = async () => {
		if ( !invoice?.id ) {
			// insulate against the ID being missing, like in slow connections
			return;
		}
		const response = await exportInvoice( {
			variables: {
				where: {
					id: invoice?.id,
				},
				data: {
					timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
					token: guestToken,
				},
			},
		} );
		if ( response.data?.exportInvoice ) {
			const { url } = response.data.exportInvoice;
			if ( url ) {
				router.push( url );
			}
		}
	};

	return {
		button: {
			EXPORT: {
				isVisible: true,
				isIconWhenSmall: true,
				variant: 'contained',
				color: 'secondary',
				'data-cy': 'export-button',
				loading,
				startIcon: <Icon type={ 'export' } />,
				onClick,
				disabled: !!invoiceEdits || !invoice?.id,
				popover: invoiceEdits
					? 'Please save your edits before taking this action'
					: undefined,
			},
		},
		exportButtonIsLoading: loading,
	};
};
